import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./PageEditor.css";
import useCmsStore from "../../../services/useCmsStore";
import { toast } from "react-toastify";
import {
  FiArrowLeft,
  FiSave,
  FiEye,
  FiSend,
  FiClock,
  FiSettings,
  FiImage,
  FiSearch,
  FiLink,
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiFileText,
  FiLayout,
  FiGlobe,
} from "react-icons/fi";
import TipTapEditor from "../Editor/TipTapEditor";
import { MediaPicker, MediaLibrary } from "../Media";
import { SeoPanel } from "../SEO";
import { AIGenerateButton } from "../AI";
import { PreviewPanel } from "../Preview";

const PageEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";

  const {
    selectedPage,
    pagesLoading,
    fetchPage,
    createPage,
    updatePage,
    publishPage,
    calculateSeoScore,
    seoScore,
    clearSelectedPage,
  } = useCmsStore();

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    content: null,
    excerpt: "",
    status: "draft",
    page_type: "page",
    template: "default",
    parent_id: null,
    featured_image_id: null,
    featured_image: null,
    // SEO
    seo_title: "",
    seo_description: "",
    seo_keywords: [],
    focus_keyword: "",
    robots_index: true,
    robots_follow: true,
    canonical_url: "",
    // Open Graph
    og_title: "",
    og_description: "",
    og_image_id: null,
    // Sitemap
    sitemap_priority: 0.5,
    sitemap_frequency: "weekly",
    exclude_from_sitemap: false,
    // Custom
    custom_css: "",
    custom_js: "",
  });

  const [expandedSections, setExpandedSections] = useState({
    general: true,
    seo: true,
    og: false,
    sitemap: false,
    advanced: false,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [keywordInput, setKeywordInput] = useState("");

  // Load page data
  useEffect(() => {
    if (!isNew && id) {
      fetchPage(id);
    }
    return () => clearSelectedPage();
  }, [id, isNew, fetchPage, clearSelectedPage]);

  // Populate form when page is loaded
  useEffect(() => {
    if (selectedPage && !isNew) {
      setFormData({
        title: selectedPage.title || "",
        slug: selectedPage.slug || "",
        content: selectedPage.content || null,
        excerpt: selectedPage.excerpt || "",
        status: selectedPage.status || "draft",
        page_type: selectedPage.page_type || "page",
        template: selectedPage.template || "default",
        parent_id: selectedPage.parent_id || null,
        featured_image_id: selectedPage.featured_image_id || null,
        seo_title: selectedPage.seo?.title || "",
        seo_description: selectedPage.seo?.description || "",
        seo_keywords: selectedPage.seo?.keywords || [],
        focus_keyword: selectedPage.seo?.focus_keyword || "",
        robots_index: selectedPage.seo?.robots_index ?? true,
        robots_follow: selectedPage.seo?.robots_follow ?? true,
        canonical_url: selectedPage.seo?.canonical_url || "",
        og_title: selectedPage.og?.title || "",
        og_description: selectedPage.og?.description || "",
        og_image_id: selectedPage.og?.image_id || null,
        sitemap_priority: selectedPage.sitemap?.priority || 0.5,
        sitemap_frequency: selectedPage.sitemap?.frequency || "weekly",
        exclude_from_sitemap: selectedPage.sitemap?.exclude || false,
        custom_css: selectedPage.custom_css || "",
        custom_js: selectedPage.custom_js || "",
      });
    }
  }, [selectedPage, isNew]);

  // Auto-generate slug from title
  const generateSlug = useCallback((title) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Auto-generate slug when title changes (only for new pages or empty slug)
      if (field === "title" && (isNew || !prev.slug)) {
        newData.slug = generateSlug(value);
      }

      // Update featured_image_id when featured_image changes
      if (field === "featured_image") {
        newData.featured_image_id = value?.id || null;
      }

      return newData;
    });
    setHasChanges(true);
  };

  const handleSave = async (publish = false) => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setIsSaving(true);
    try {
      const dataToSave = {
        ...formData,
        status: publish ? "published" : formData.status,
      };

      if (isNew) {
        const newPage = await createPage(dataToSave);
        toast.success("Page created successfully");
        navigate(`/cms/pages/${newPage.id}`);
      } else {
        await updatePage(id, dataToSave);
        toast.success(publish ? "Page published successfully" : "Page saved successfully");
      }
      setHasChanges(false);
    } catch (error) {
      toast.error(error.message || "Failed to save page");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!isNew && formData.status !== "published") {
      try {
        await publishPage(id);
        toast.success("Page published successfully");
        setHasChanges(false);
      } catch (error) {
        toast.error("Failed to publish page");
      }
    } else {
      handleSave(true);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.seo_keywords.includes(keywordInput.trim())) {
      handleChange("seo_keywords", [...formData.seo_keywords, keywordInput.trim()]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (keyword) => {
    handleChange(
      "seo_keywords",
      formData.seo_keywords.filter((k) => k !== keyword)
    );
  };

  const getSeoScoreColor = () => {
    if (!seoScore) return "#6B7280";
    if (seoScore.score >= 70) return "#10B981";
    if (seoScore.score >= 40) return "#F59E0B";
    return "#EF4444";
  };

  // Calculate SEO score when relevant fields change
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (formData.title || formData.content) {
        calculateSeoScore({
          title: formData.title,
          content: formData.content,
          excerpt: formData.excerpt,
          seo_title: formData.seo_title,
          seo_description: formData.seo_description,
          focus_keyword: formData.focus_keyword,
        });
      }
    }, 1000);

    return () => clearTimeout(debounce);
  }, [formData.title, formData.content, formData.excerpt, formData.seo_title, formData.seo_description, formData.focus_keyword, calculateSeoScore]);

  if (pagesLoading && !isNew) {
    return (
      <div className="page-editor-loading">
        <div className="loading-spinner"></div>
        <p>Loading page...</p>
      </div>
    );
  }

  return (
    <div className="page-editor">
      {/* Header */}
      <div className="page-editor-header">
        <div className="page-editor-header-left">
          <button className="btn-back" onClick={() => navigate("/cms")}>
            <FiArrowLeft size={20} />
          </button>
          <div className="page-editor-title-section">
            <h1>{isNew ? "New Page" : "Edit Page"}</h1>
            {hasChanges && <span className="unsaved-badge">Unsaved changes</span>}
          </div>
        </div>
        <div className="page-editor-header-right">
          <button
            className="btn-secondary"
            onClick={() => setShowPreview(!showPreview)}
          >
            <FiEye size={16} />
            <span>Preview</span>
          </button>
          <button
            className="btn-secondary"
            onClick={() => handleSave(false)}
            disabled={isSaving}
          >
            <FiSave size={16} />
            <span>{isSaving ? "Saving..." : "Save Draft"}</span>
          </button>
          <button
            className="btn-primary"
            onClick={handlePublish}
            disabled={isSaving}
          >
            <FiSend size={16} />
            <span>Publish</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-editor-main">
        {/* Left Panel - Settings */}
        <div className="page-editor-sidebar">
          {/* SEO Analysis Panel */}
          <SeoPanel
            title={formData.title}
            content={formData.content}
            excerpt={formData.excerpt}
            focusKeyword={formData.focus_keyword}
            seoTitle={formData.seo_title}
            seoDescription={formData.seo_description}
            slug={formData.slug}
          />

          {/* General Settings */}
          <div className="settings-section">
            <button
              className="section-header"
              onClick={() => toggleSection("general")}
            >
              <div className="section-title">
                <FiSettings size={16} />
                <span>General Settings</span>
              </div>
              {expandedSections.general ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {expandedSections.general && (
              <div className="section-content">
                <div className="form-group">
                  <label>Page Type</label>
                  <select
                    value={formData.page_type}
                    onChange={(e) => handleChange("page_type", e.target.value)}
                  >
                    <option value="page">Standard Page</option>
                    <option value="landing">Landing Page</option>
                    <option value="contact">Contact Page</option>
                    <option value="about">About Page</option>
                    <option value="services">Services Page</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Template</label>
                  <select
                    value={formData.template}
                    onChange={(e) => handleChange("template", e.target.value)}
                  >
                    <option value="default">Default</option>
                    <option value="full-width">Full Width</option>
                    <option value="sidebar-left">Sidebar Left</option>
                    <option value="sidebar-right">Sidebar Right</option>
                    <option value="landing">Landing</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* SEO Settings */}
          <div className="settings-section">
            <button
              className="section-header"
              onClick={() => toggleSection("seo")}
            >
              <div className="section-title">
                <FiSearch size={16} />
                <span>SEO Settings</span>
              </div>
              {expandedSections.seo ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {expandedSections.seo && (
              <div className="section-content">
                <div className="form-group">
                  <label>
                    Focus Keyword
                    <span className="label-hint">Main keyword for this page</span>
                  </label>
                  <input
                    type="text"
                    value={formData.focus_keyword}
                    onChange={(e) => handleChange("focus_keyword", e.target.value)}
                    placeholder="e.g., digital marketing"
                  />
                </div>
                <div className="form-group">
                  <label>
                    SEO Title
                    <span className="char-count" style={{
                      color: formData.seo_title.length > 60 ? "#EF4444" : "#6B7280"
                    }}>
                      {formData.seo_title.length}/60
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.seo_title}
                    onChange={(e) => handleChange("seo_title", e.target.value)}
                    placeholder={formData.title || "Enter SEO title"}
                    maxLength={70}
                  />
                </div>
                <div className="form-group">
                  <label>
                    Meta Description
                    <span className="char-count" style={{
                      color: formData.seo_description.length > 160 ? "#EF4444" : "#6B7280"
                    }}>
                      {formData.seo_description.length}/160
                    </span>
                  </label>
                  <textarea
                    value={formData.seo_description}
                    onChange={(e) => handleChange("seo_description", e.target.value)}
                    placeholder="Enter meta description..."
                    rows={3}
                    maxLength={160}
                  />
                </div>
                <div className="form-group">
                  <label>SEO Keywords</label>
                  <div className="keyword-input-wrapper">
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                      placeholder="Add keyword and press Enter"
                    />
                    <button type="button" onClick={addKeyword}>Add</button>
                  </div>
                  {formData.seo_keywords.length > 0 && (
                    <div className="keywords-list">
                      {formData.seo_keywords.map((keyword, idx) => (
                        <span key={idx} className="keyword-tag">
                          {keyword}
                          <button onClick={() => removeKeyword(keyword)}>
                            <FiX size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Canonical URL</label>
                  <input
                    type="url"
                    value={formData.canonical_url}
                    onChange={(e) => handleChange("canonical_url", e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="form-group-inline">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.robots_index}
                      onChange={(e) => handleChange("robots_index", e.target.checked)}
                    />
                    <span>Allow search engines to index</span>
                  </label>
                </div>
                <div className="form-group-inline">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.robots_follow}
                      onChange={(e) => handleChange("robots_follow", e.target.checked)}
                    />
                    <span>Allow search engines to follow links</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Open Graph Settings */}
          <div className="settings-section">
            <button
              className="section-header"
              onClick={() => toggleSection("og")}
            >
              <div className="section-title">
                <FiGlobe size={16} />
                <span>Social Sharing</span>
              </div>
              {expandedSections.og ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {expandedSections.og && (
              <div className="section-content">
                <div className="form-group">
                  <label>OG Title</label>
                  <input
                    type="text"
                    value={formData.og_title}
                    onChange={(e) => handleChange("og_title", e.target.value)}
                    placeholder={formData.seo_title || formData.title || "Enter OG title"}
                  />
                </div>
                <div className="form-group">
                  <label>OG Description</label>
                  <textarea
                    value={formData.og_description}
                    onChange={(e) => handleChange("og_description", e.target.value)}
                    placeholder={formData.seo_description || "Enter OG description"}
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sitemap Settings */}
          <div className="settings-section">
            <button
              className="section-header"
              onClick={() => toggleSection("sitemap")}
            >
              <div className="section-title">
                <FiLayout size={16} />
                <span>Sitemap</span>
              </div>
              {expandedSections.sitemap ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {expandedSections.sitemap && (
              <div className="section-content">
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={formData.sitemap_priority}
                    onChange={(e) => handleChange("sitemap_priority", parseFloat(e.target.value))}
                  >
                    <option value={1.0}>1.0 - Highest</option>
                    <option value={0.8}>0.8 - High</option>
                    <option value={0.5}>0.5 - Normal</option>
                    <option value={0.3}>0.3 - Low</option>
                    <option value={0.1}>0.1 - Lowest</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Change Frequency</label>
                  <select
                    value={formData.sitemap_frequency}
                    onChange={(e) => handleChange("sitemap_frequency", e.target.value)}
                  >
                    <option value="always">Always</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="never">Never</option>
                  </select>
                </div>
                <div className="form-group-inline">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.exclude_from_sitemap}
                      onChange={(e) => handleChange("exclude_from_sitemap", e.target.checked)}
                    />
                    <span>Exclude from sitemap</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Advanced Settings */}
          <div className="settings-section">
            <button
              className="section-header"
              onClick={() => toggleSection("advanced")}
            >
              <div className="section-title">
                <FiFileText size={16} />
                <span>Advanced</span>
              </div>
              {expandedSections.advanced ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {expandedSections.advanced && (
              <div className="section-content">
                <div className="form-group">
                  <label>Custom CSS</label>
                  <textarea
                    value={formData.custom_css}
                    onChange={(e) => handleChange("custom_css", e.target.value)}
                    placeholder=".custom-class { }"
                    rows={4}
                    className="code-textarea"
                  />
                </div>
                <div className="form-group">
                  <label>Custom JavaScript</label>
                  <textarea
                    value={formData.custom_js}
                    onChange={(e) => handleChange("custom_js", e.target.value)}
                    placeholder="// Your custom JS"
                    rows={4}
                    className="code-textarea"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Content Editor */}
        <div className="page-editor-content">
          <div className="content-header">
            <div className="form-group title-with-ai">
              <label>Title</label>
              <div className="title-input-row">
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="Enter page title..."
                  className="title-input"
                />
                <AIGenerateButton
                  title={formData.title}
                  type="page"
                  language="pt-BR"
                  pageType={formData.page_type}
                  onGenerated={(result) => {
                    if (result.content) handleChange("content", result.content);
                    if (result.excerpt) handleChange("excerpt", result.excerpt);
                    if (result.seo_description) handleChange("seo_description", result.seo_description);
                    toast.success("Content generated successfully!");
                  }}
                />
              </div>
            </div>
            <div className="form-group slug-group">
              <label>
                <FiLink size={14} />
                <span>Slug</span>
              </label>
              <div className="slug-input-wrapper">
                <span className="slug-prefix">/</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleChange("slug", e.target.value)}
                  placeholder="page-slug"
                />
              </div>
            </div>
          </div>

          <div className="content-body">
            <div className="form-group">
              <label>Excerpt</label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => handleChange("excerpt", e.target.value)}
                placeholder="Brief summary of the page..."
                rows={3}
              />
            </div>

            <div className="form-group content-editor-group">
              <label>Content</label>
              <TipTapEditor
                content={formData.content}
                onChange={(content) => handleChange("content", content)}
                placeholder="Start writing your page content or type / for commands..."
                onImageUpload={async (file) => {
                  // TODO: Integrate with media library upload
                  return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.readAsDataURL(file);
                  });
                }}
              />
            </div>
          </div>

          {/* Featured Image */}
          <div className="featured-image-section">
            <MediaPicker
              value={formData.featured_image}
              onChange={(media) => handleChange("featured_image", media)}
              type="image"
              label="Featured Image"
              placeholder="Click to select a featured image"
            />
          </div>
        </div>
      </div>

      {/* Preview Panel */}
      <PreviewPanel
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        content={formData.content}
        title={formData.title}
        excerpt={formData.excerpt}
        featuredImage={formData.featured_image}
      />
    </div>
  );
};

export default PageEditor;
